import React, { useRef, useState } from "react";
import { Sparkles, ArrowRight, Loader2, Upload, Check, Wand2, MessageCircle, CheckCircle2, Info, ImagePlus } from "lucide-react";
import { useGet } from "@/lib/hooks";
import { api, uploadMedia, absUrl, getGuestId } from "@/lib/api";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { ProtectedImage } from "@/components/wp/ProtectedImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TYPES = ["Necklace", "Pendant", "Earrings", "Bracelet", "Other"];
const RECIPIENTS = ["Partner", "Mother", "Father", "Sister", "Brother", "Friend", "Daughter", "Son", "Other"];
const OCCASIONS = ["Birthday", "Anniversary", "Wedding", "Milestone", "Thank You", "Celebration", "Other"];
const EMPTY_ENQUIRY = { name: "", phone: "", email: "", city: "", occasion: "", budget: "", notes: "" };

export default function CustomSilver() {
  const { data: cs, loading } = useGet("/custom-silver");

  // Generator state
  const [gen, setGen] = useState({ jewellery_type: "", recipient: "", occasion: "", idea: "" });
  const [refine, setRefine] = useState("");
  const [inspiration, setInspiration] = useState(null); // { url }
  const [uploading, setUploading] = useState(false);
  const [concepts, setConcepts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);

  // Enquiry state
  const [form, setForm] = useState(EMPTY_ENQUIRY);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const genRef = useRef(null);
  const conceptsRef = useRef(null);
  const enquiryRef = useRef(null);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const data = await uploadMedia(file, "photo", getGuestId()); setInspiration({ url: absUrl(data.media_url) }); toast.success("Inspiration image added"); }
    catch { toast.error("Could not upload image"); }
    finally { setUploading(false); }
  };

  const generate = async (withRefine = false) => {
    if (!gen.idea.trim()) { toast.error("Please describe your idea first."); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/custom-silver/generate", {
        jewellery_type: gen.jewellery_type,
        recipient: gen.recipient,
        occasion: gen.occasion,
        idea: gen.idea,
        refine: withRefine ? refine : "",
        base_direction: withRefine ? (selected?.caption || "") : "",
        count: 3,
      }, { timeout: 150000 });
      setConcepts(data.concepts || []);
      setSelected(null);
      setTimeout(() => scrollTo(conceptsRef), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "We couldn't create design ideas right now. Please try again.");
    } finally { setGenerating(false); }
  };

  const selectConcept = (c) => {
    setSelected(c);
    setForm((f) => ({ ...f }));
    setTimeout(() => scrollTo(enquiryRef), 100);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (result || submitting) return; // prevent duplicate enquiries
    if (!form.name.trim()) return toast.error("Please share your name.");
    if (!form.phone.trim()) return toast.error("Please share your phone / WhatsApp.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/custom-silver/enquiry", {
        ...form,
        jewellery_type: gen.jewellery_type,
        idea: gen.idea,
        concept_image: selected?.image_url ? absUrl(selected.image_url) : "",
        concept_caption: selected?.caption || "",
        concept_ref: selected?.ref || "",
        inspiration_image: inspiration?.url || "",
      });
      setResult(data);
      toast.success("Thank you — your design enquiry has been received.");
      // Enquiry saved — attempt the WhatsApp handoff automatically (fallback button remains).
      if (data.whatsapp_url) {
        try { window.open(data.whatsapp_url, "_blank", "noopener"); } catch { /* fallback */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="wp-container wp-section">
        <Skeleton className="mx-auto h-12 w-2/3 rounded-lg" />
        <Skeleton className="mx-auto mt-4 h-5 w-1/2 rounded-lg" />
        <div className="mt-12 grid gap-6 sm:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="aspect-square w-full rounded-wp-lg" />)}</div>
      </div>
    );
  }

  if (cs && cs.enabled === false) {
    return (
      <div className="wp-container wp-section text-center">
        <Sparkles className="mx-auto h-10 w-10 text-wp-rose-strong" />
        <h1 className="mt-4 font-display text-2xl text-wp-ink">Custom Silver Gifts</h1>
        <p className="mt-2 text-wp-ink-muted">This experience is coming soon. Please check back shortly.</p>
      </div>
    );
  }

  const c = cs || {};

  return (
    <div data-testid="custom-silver-page" className="bg-wp-bg">
      {/* ================= HERO ================= */}
      <section className="bg-gradient-to-b from-wp-cream-100 to-wp-bg">
        <div className="wp-container py-12 md:py-16">
          <div className="mx-auto max-w-3xl text-center">
            <span className="wp-label inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-xs text-wp-rose-strong shadow-wp-soft">
              <Sparkles className="h-3.5 w-3.5" /> Custom Silver Gifts
            </span>
            <h1 className="mt-6 font-display text-4xl leading-tight text-wp-rose-strong md:text-6xl" data-testid="silver-headline">{c.headline}</h1>
            <p className="mx-auto mt-5 max-w-2xl text-base text-wp-ink-muted md:text-lg">{c.subheading}</p>
            <p className="mt-4 text-sm font-medium tracking-wide text-wp-ink">{c.supporting_line}</p>
            <div className="mx-auto mt-8 max-w-xl rounded-wp-lg border border-wp-gold/40 bg-wp-gold/10 px-6 py-5">
              <p className="font-display text-lg tracking-wide text-wp-rose-strong" data-testid="silver-starting-price">{c.starting_price_text}</p>
              <p className="mt-1.5 text-xs text-wp-ink-muted">{c.price_note}</p>
            </div>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button onClick={() => scrollTo(genRef)} className="rounded-xl bg-wp-rose-strong px-8 py-6 text-base text-white hover:brightness-110" data-testid="silver-hero-primary">
                {c.primary_cta || "Create a Design Idea"} <ArrowRight className="ml-1.5 h-5 w-5" />
              </Button>
              <Button variant="outline" onClick={() => scrollTo(enquiryRef)} className="rounded-xl border-wp-rose-strong px-8 py-6 text-base text-wp-rose-strong hover:bg-wp-cream-100" data-testid="silver-hero-secondary">
                {c.secondary_cta || "Talk to Us"}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SAMPLES ================= */}
      <section className="wp-section">
        <div className="wp-container">
          <SectionHeading eyebrow="Design inspiration" title="Crafted in silver, made personal" align="center" />
          <div className="mx-auto mt-10 grid max-w-4xl gap-5 sm:grid-cols-3">
            {(c.samples || []).map((s, i) => (
              <figure key={i} className="overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-soft" data-testid={`silver-sample-${i}`}>
                <div className="aspect-square overflow-hidden bg-wp-cream-100">
                  <ProtectedImage src={s.image} alt={s.label} className="h-full w-full object-cover" />
                </div>
                <figcaption className="px-4 py-3 text-center text-sm text-wp-ink-muted">{s.label}</figcaption>
              </figure>
            ))}
          </div>
          <p className="mx-auto mt-5 max-w-xl text-center text-sm italic text-wp-ink-muted" data-testid="silver-sample-note">{c.sample_note}</p>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="wp-section bg-wp-cream-100">
        <div className="wp-container">
          <SectionHeading eyebrow="Simple & personal" title="How it works" align="center" />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(c.how_it_works || []).map((step, i) => (
              <div key={i} className="rounded-wp-lg border border-border bg-card p-6 shadow-wp-soft" data-testid={`silver-step-${i}`}>
                <div className="grid h-10 w-10 place-items-center rounded-full bg-wp-rose-strong font-display text-lg text-white">{i + 1}</div>
                <h3 className="mt-4 font-display text-lg text-wp-ink">{step.title}</h3>
                <p className="mt-2 text-sm text-wp-ink-muted">{step.copy}</p>
              </div>
            ))}
          </div>
          {(c.story_title || c.story_copy) && (
            <div className="mx-auto mt-10 max-w-3xl rounded-wp-lg border border-wp-gold/40 bg-wp-gold/10 p-6 text-center md:p-8" data-testid="silver-story">
              {c.story_title && <h3 className="font-display text-xl text-wp-rose-strong md:text-2xl">{c.story_title}</h3>}
              {c.story_copy && <p className="mx-auto mt-3 max-w-2xl text-sm text-wp-ink-muted md:text-base">{c.story_copy}</p>}
              {c.review_note && <p className="mt-4 text-xs italic text-wp-ink-muted" data-testid="silver-review-note">{c.review_note}</p>}
            </div>
          )}
        </div>
      </section>

      {/* ================= GENERATOR ================= */}
      <section ref={genRef} className="wp-section">
        <div className="wp-container">
          <SectionHeading eyebrow="Describe your idea" title="Create your design idea" subtitle="Tell us your idea and we'll show you a few design directions to explore." align="center" />
          <div className="mx-auto mt-8 max-w-2xl rounded-wp-lg border border-border bg-card p-6 shadow-wp-soft md:p-8" data-testid="silver-generator">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-sm">What would you like to create?</Label>
                <Select value={gen.jewellery_type} onValueChange={(v) => setGen((g) => ({ ...g, jewellery_type: v }))}>
                  <SelectTrigger className="rounded-wp-md" data-testid="silver-input-type"><SelectValue placeholder="Choose a type" /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">Who is it for? <span className="text-wp-ink-muted">(optional)</span></Label>
                <Select value={gen.recipient} onValueChange={(v) => setGen((g) => ({ ...g, recipient: v }))}>
                  <SelectTrigger className="rounded-wp-md" data-testid="silver-input-recipient"><SelectValue placeholder="Choose (optional)" /></SelectTrigger>
                  <SelectContent>{RECIPIENTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">Occasion <span className="text-wp-ink-muted">(optional)</span></Label>
                <Select value={gen.occasion} onValueChange={(v) => setGen((g) => ({ ...g, occasion: v }))}>
                  <SelectTrigger className="rounded-wp-md" data-testid="silver-input-occasion"><SelectValue placeholder="Choose (optional)" /></SelectTrigger>
                  <SelectContent>{OCCASIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label className="mb-1.5 block text-sm">Describe your idea</Label>
              <Textarea rows={3} value={gen.idea} onChange={(e) => setGen((g) => ({ ...g, idea: e.target.value }))}
                placeholder="A delicate silver pendant for my wife inspired by the moon and our wedding date, with a minimal timeless design."
                className="rounded-wp-md" data-testid="silver-input-idea" />
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-wp-ink-muted"><Info className="h-3.5 w-3.5" /> {c.ip_note}</p>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-wp-md border border-border px-3 py-2 text-sm text-wp-ink hover:border-wp-rose-strong" data-testid="silver-upload-label">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload an inspiration image
                <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])} />
              </label>
              {inspiration && <img src={inspiration.url} alt="inspiration" className="h-10 w-10 rounded-md object-cover" />}
              <span className="text-xs text-wp-ink-muted">Please upload images you own or have permission to use.</span>
            </div>
            <Button onClick={() => generate(false)} disabled={generating} className="mt-6 w-full rounded-xl bg-wp-rose-strong py-6 text-base text-white hover:brightness-110 disabled:opacity-60" data-testid="silver-generate-button">
              {generating ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Creating design ideas…</> : <><Wand2 className="mr-2 h-5 w-5" /> Show Me Design Ideas</>}
            </Button>
          </div>
        </div>
      </section>

      {/* ================= CONCEPTS ================= */}
      {concepts.length > 0 && (
        <section ref={conceptsRef} className="wp-section bg-wp-cream-100">
          <div className="wp-container">
            <SectionHeading eyebrow="Explore directions" title="Design concepts" align="center" />
            <p className="mx-auto mt-3 max-w-2xl text-center text-xs italic text-wp-ink-muted" data-testid="silver-concept-disclaimer">{c.concept_disclaimer}</p>
            <div className="mx-auto mt-8 grid max-w-4xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {concepts.map((cc) => (
                <div key={cc.id} className={`overflow-hidden rounded-wp-lg border bg-card shadow-wp-soft transition ${selected?.id === cc.id ? "border-wp-rose-strong ring-2 ring-wp-rose-strong/30" : "border-border"}`} data-testid={`silver-concept-${cc.id}`}>
                  <div className="relative aspect-square overflow-hidden bg-wp-cream-100">
                    <ProtectedImage src={absUrl(cc.image_url)} alt="Design concept" className="h-full w-full object-cover" />
                    <span className="absolute left-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">Design concept</span>
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-medium text-wp-ink">{cc.caption}</p>
                    <Button onClick={() => selectConcept(cc)} className="mt-3 w-full rounded-xl bg-wp-rose-strong text-white hover:brightness-110" data-testid={`silver-select-${cc.id}`}>
                      {selected?.id === cc.id ? <><Check className="mr-1.5 h-4 w-4" /> Selected</> : "Select this concept"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Refine */}
            <div className="mx-auto mt-8 max-w-2xl rounded-wp-lg border border-border bg-card p-5 shadow-wp-soft">
              <Label className="mb-1.5 block text-sm">Refine your idea <span className="text-wp-ink-muted">(optional)</span></Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input value={refine} onChange={(e) => setRefine(e.target.value)} placeholder="e.g. Make it more minimal, use a smaller pendant…" className="rounded-wp-md" data-testid="silver-refine-input" />
                <Button variant="outline" onClick={() => generate(true)} disabled={generating} className="rounded-xl border-wp-rose-strong text-wp-rose-strong hover:bg-wp-cream-100" data-testid="silver-refine-button">
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refine"}
                </Button>
                <Button variant="ghost" onClick={() => generate(false)} disabled={generating} className="rounded-xl" data-testid="silver-try-another">Try another direction</Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ================= ENQUIRY ================= */}
      <section ref={enquiryRef} className="wp-section">
        <div className="wp-container">
          <div className="mx-auto max-w-2xl">
            <SectionHeading eyebrow="Next step" title="Request a consultation" align="center" />
            {result ? (
              <div className="mt-8 rounded-wp-lg border border-border bg-card p-8 text-center shadow-wp-soft" data-testid="silver-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-wp-success" />
                <h3 className="mt-4 font-display text-2xl text-wp-ink">Your design request is with us.</h3>
                {result.reference && <p className="mt-1 text-sm font-medium text-wp-ink">Enquiry <span className="font-semibold">#{result.reference}</span></p>}
                <p className="mt-2 text-wp-ink-muted">We'll review your selected concept with our crafting partner and confirm any small adjustments needed for proportions, budget and production feasibility before sharing the final quotation.</p>
                <a href={result.whatsapp_url} target="_blank" rel="noreferrer" data-testid="silver-whatsapp-button" className="mt-6 inline-block">
                  <Button className="rounded-xl bg-wp-rose-strong px-6 py-5 text-white hover:brightness-110"><MessageCircle className="mr-2 h-5 w-5" /> Continue on WhatsApp</Button>
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 rounded-wp-lg border border-border bg-card p-6 shadow-wp-soft md:p-8" data-testid="silver-enquiry-form">
                {selected && (
                  <div className="mb-5 flex items-center gap-3 rounded-wp-md border border-wp-gold/40 bg-wp-gold/10 p-3" data-testid="silver-selected-concept">
                    <img src={absUrl(selected.image_url)} alt="selected concept" className="h-16 w-16 rounded-md object-cover" />
                    <div><p className="text-xs uppercase tracking-wide text-wp-ink-muted">Selected concept</p><p className="text-sm font-medium text-wp-ink">{selected.caption}</p></div>
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label className="mb-1.5 block text-sm">Name <span className="text-wp-rose-strong">*</span></Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="rounded-wp-md" data-testid="silver-form-name" /></div>
                  <div><Label className="mb-1.5 block text-sm">Phone / WhatsApp <span className="text-wp-rose-strong">*</span></Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="rounded-wp-md" data-testid="silver-form-phone" /></div>
                  <div><Label className="mb-1.5 block text-sm">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="rounded-wp-md" data-testid="silver-form-email" /></div>
                  <div><Label className="mb-1.5 block text-sm">City</Label><Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className="rounded-wp-md" data-testid="silver-form-city" /></div>
                  <div><Label className="mb-1.5 block text-sm">Occasion</Label><Input value={form.occasion} onChange={(e) => setForm((f) => ({ ...f, occasion: e.target.value }))} className="rounded-wp-md" data-testid="silver-form-occasion" /></div>
                  <div>
                    <Label className="mb-1.5 block text-sm">Approximate budget</Label>
                    <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                      <SelectTrigger className="rounded-wp-md" data-testid="silver-form-budget"><SelectValue placeholder="Select a budget range" /></SelectTrigger>
                      <SelectContent>{(c.budgets || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2"><Label className="mb-1.5 block text-sm">Additional notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="rounded-wp-md" data-testid="silver-form-notes" /></div>
                </div>
                <p className="mt-4 text-center text-xs italic text-wp-ink-muted" data-testid="silver-price-qualifier">{c.price_qualifier}</p>
                <Button type="submit" disabled={submitting} className="mt-4 w-full rounded-xl bg-wp-rose-strong py-6 text-base text-white hover:brightness-110 disabled:opacity-60" data-testid="silver-submit">
                  {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</> : <>Request a Consultation <ArrowRight className="ml-1.5 h-5 w-5" /></>}
                </Button>
              </form>
            )}

            {/* Disclaimers */}
            <div className="mt-6 space-y-2 rounded-wp-md border border-border bg-wp-cream-100 p-4 text-xs text-wp-ink-muted">
              <p data-testid="silver-manufacturing-disclaimer">{c.manufacturing_disclaimer}</p>
              <p data-testid="silver-certification-text">{c.certification_text}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
